import React, { useEffect } from 'react';

import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import type { GridSortChangeEvent } from 'pages/reports/components/sortUtils';
import { createSortChangeHandler } from 'pages/reports/components/sortUtils';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { buildExportFileName, GenerateUUID } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { EResourceType } from 'pages/reports/types';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { Card, Table } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/ChartInfoBox/styles/chart-info-box.scss';
import 'pages/reports/components/Report68/Report68.scss';

import {
    calculateHourMinFormat,
    formatDate,
    getStatus,
    getUniqueStudentNames,
    MAX_TABLE_HEIGHT,
    normalizeScore,
    renderScore,
    renderStatus,
    ResourceTypeLabelsList,
    sortableHeader,
    useResourceTypeLabelsList
} from '../ReportFunctions';
import { CourseSummaryCard } from './CourseSummaryCard';

interface Report68Props {
    reportSections: ReportSection[];
}

type MetricType = 'Outcomes' | 'Time Spent' | 'Attempts';

export const Report68 = ({
    reportSections
}: Report68Props) => {

    // State Management
    const resourceTypeLabels = useResourceTypeLabelsList();

    const [state, setState] = useSuperState({
        currentMetric: 'Outcomes' as MetricType,
        exportFileName: 'CourseSummaryReport_Student_Outcomes.csv',
        exportRows: [] as any[],
        showResourcesSelected: resourceTypeLabels,
        sort: [] as SortDescriptor[],
        students: [] as any[],
        tableKey: 'tableKey',
        tableRows: [] as any[]
    });

    const { currentMetric, exportFileName, exportRows, showResourcesSelected, sort, students, tableKey, tableRows } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // getStatus returns canonical identifiers; use this wherever the status is shown as plain text.
    const statusLabel = (status: string) => status === 'Complete'
        ? formatMessage({ id: 'complete.6757643c', defaultMessage: 'Complete' })
        : status === 'In Progress'
            ? formatMessage({ id: 'in_progress.0e4b5895', defaultMessage: 'In Progress' })
            : formatMessage({ id: 'not_started.6c6f679c', defaultMessage: 'Not Started' });

    // Filter rows based on selected resource types
    const selectedValues = showResourcesSelected.map(selected => selected.value);
    const allKnownValues = ResourceTypeLabelsList.map(r => r.value);
    const filteredRows = tableRows.filter(row => selectedValues.includes(row.ResourceTypeID) || !allKnownValues.includes(row.ResourceTypeID)
    );

    const rowsInDisplayOrder = React.useMemo(() => [...filteredRows].sort((a: any, b: any) => {

        const aLessonDisplayOrder = a.LessonDisplayOrder ?? 0;
        const bLessonDisplayOrder = b.LessonDisplayOrder ?? 0;

        if (aLessonDisplayOrder !== bLessonDisplayOrder) {

            return aLessonDisplayOrder - bLessonDisplayOrder;

        }

        const aResourceDisplayOrder = a.ResourceDisplayOrder ?? a.DisplayOrder ?? 0;
        const bResourceDisplayOrder = b.ResourceDisplayOrder ?? b.DisplayOrder ?? 0;

        if (aResourceDisplayOrder !== bResourceDisplayOrder) {

            return aResourceDisplayOrder - bResourceDisplayOrder;

        }

        const aName = (a.ResourceName ?? '').toString();
        const bName = (b.ResourceName ?? '').toString();
        return aName.localeCompare(bName);

    }), [filteredRows]);

    const sortedData = sort.length > 0 ? orderBy(rowsInDisplayOrder, sort) : rowsInDisplayOrder;
    const displayData = sortedData.slice(0, 500);

    const getStudentResourceMetric = (student: any, resourceId: number) => {

        const lesson = student.Lessons?.find((l: any) => l.Resources?.some((r: any) => r.ResourceId === resourceId));
        const resource = lesson?.Resources?.find((r: any) => r.ResourceId === resourceId);
        if (!resource) return null;
        switch (currentMetric) {

            case 'Time Spent':
                return resource.TimeSpentInMinutes ?? 0;
            case 'Attempts':
                return resource.Attempts ?? 0;
            case 'Outcomes':
            default:
                return normalizeScore(resource.Completed?.Score ?? resource.HighestScore ?? 0);

        }

    };

    const getResourceStudentMetric = (resourceRow: any, studentId: number) => {

        const studentData = resourceRow.StudentData?.find((s: any) => s.StudentProfileId === studentId);
        if (!studentData) return null;
        switch (currentMetric) {

            case 'Time Spent':
                return studentData.TimeSpent ?? 0;
            case 'Attempts':
                return studentData.Attempts ?? 0;
            case 'Outcomes':
            default:
                return normalizeScore(studentData.Score ?? 0);

        }

    };

    const getSortableValue = (item: any, field?: string) => {

        if (!field) return null;
        if (field.startsWith('resource-')) {

            const resourceId = Number(field.replace('resource-', ''));
            return getStudentResourceMetric(item, resourceId);

        }
        if (field.startsWith('student-')) {

            const studentId = Number(field.replace('student-', ''));
            return getResourceStudentMetric(item, studentId);

        }
        if (field === 'CourseLastAccessDate' || field === 'CourseDateCompleted') {

            return item[field] ? new Date(item[field]).getTime() : null;

        }
        if (field === 'AverageScore') return normalizeScore(Number(item[field]));
        const value = item[field];
        return value;

    };

    const handleSortChange = (event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    };

    const buildStudentExport = () => {

        const rows = filteredRows.map((row: any) => ({
            [formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' })]: row.Attempts ? `${row.Attempts}x` : '---',
            [formatMessage({ id: 'last_access.6fd7ff0b', defaultMessage: 'Last Access' })]: formatDate(row.CourseLastAccessDate),
            [formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })]: resourceTypeLabels.find(r => r.value === row.ResourceTypeID)?.name ?? EResourceType[row.ResourceTypeID],
            [formatMessage({ id: 'resource_name.c8bbe57c', defaultMessage: 'Resource Name' })]: row.ResourceName || '---',
            [formatMessage({ id: 'score.6ed6415a', defaultMessage: 'Score' })]: row.HighestScore ? `${normalizeScore(row.HighestScore)}%` : '---',
            [formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })]: statusLabel(getStatus(row)),
            [formatMessage({ id: 'time_spent.da775c0c', defaultMessage: 'Time Spent' })]: calculateHourMinFormat(row.TimeSpentInMinutes ?? 0)
        }));
        return rows;

    };

    const handleExportCSV = () => {

        const reportName = reportSections[0]?.Title || 'Student Course Detail Report';

        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        const dataRows = buildStudentExport();
        setState({ exportRows: dataRows });
        setTimeout(() => {

            const csvLink = document.getElementById('csvLink');
            csvLink?.click();

        }, 0);

    };

    // Initialize data on mount
    useEffect(() => {

        setState({ students: getUniqueStudentNames(reportSections[0].Components[0].Results?.Data) });
        const rawData = reportSections[0].Components[0].Results?.Data ?? [];

        // Aggregate multiple records for the same resource (e.g., multiple attempts)
        const resourceMap: Record<number, any> = {};
        rawData.forEach((item: any) => {

            const resourceId = item.ResourceId;
            if (!resourceMap[resourceId]) {

                resourceMap[resourceId] = [];

            }
            resourceMap[resourceId].push(item);

        });

        // Aggregate the records for each resource
        const aggregatedData = Object.values(resourceMap).map((records: any) => {

            const validScores = records.map((r: any) => r.HighestScore).filter((s: any) => s !== null && s !== undefined);
            return {
                ...records[0], // Base record
                Attempts: records.reduce((sum: number, r: any) => sum + (r.Attempts || 0), 0),
                CourseDateCompleted: records.find((r: any) => r.CourseDateCompleted)?.CourseDateCompleted,
                CourseLastAccessDate: records.reduce((latest: string | null, r: any) => {

                    if (!r.CourseLastAccessDate) return latest;
                    if (!latest) return r.CourseLastAccessDate;
                    return new Date(r.CourseLastAccessDate) > new Date(latest) ? r.CourseLastAccessDate : latest;

                }, null),
                CourseSecondsSpent: records.reduce((sum: number, r: any) => sum + (r.CourseSecondsSpent || 0), 0),
                HighestScore: validScores.length > 0 ? Math.max(...validScores) : null,
                TimeSpentInMinutes: records.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0)
            };

        });

        setState({ tableRows: aggregatedData });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });

    }, [showResourcesSelected]);

    useEffect(() => {

        if (filteredRows.length > 500) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: 500, total: filteredRows.length }
                ),
                'warning'
            );

        }

    }, [filteredRows.length]);

    // Calculate summary data from all rows (not affected by filters)
    const summaryData = React.useMemo(() => {

        const rawData = reportSections[0].Components[0].Results?.Data ?? [];

        if (rawData.length === 0) {

            return {
                completedDate: null,
                lastAccess: '---',
                progress: 0,
                startedDate: '---',
                timeSpent: '0 min'
            };

        }

        // Aggregate multiple records for the same resource
        const resourceMap: Record<number, any> = {};
        rawData.forEach((item: any) => {

            const resourceId = item.ResourceId;
            if (!resourceMap[resourceId]) {

                resourceMap[resourceId] = [];

            }
            resourceMap[resourceId].push(item);

        });

        const allRows = Object.values(resourceMap).map((records: any) => {

            const validScores = records.map((r: any) => r.HighestScore).filter((s: any) => s !== null && s !== undefined);
            return {
                ...records[0],
                CourseDateCompleted: records.find((r: any) => r.CourseDateCompleted)?.CourseDateCompleted,
                CourseLastAccessDate: records.reduce((latest: string | null, r: any) => {

                    if (!r.CourseLastAccessDate) return latest;
                    if (!latest) return r.CourseLastAccessDate;
                    return new Date(r.CourseLastAccessDate) > new Date(latest) ? r.CourseLastAccessDate : latest;

                }, null),
                CourseSecondsSpent: records.reduce((sum: number, r: any) => sum + (r.CourseSecondsSpent || 0), 0),
                CourseStartedDate: records.reduce((earliest: string | null, r: any) => {

                    if (!r.CourseStartedDate) return earliest;
                    if (!earliest) return r.CourseStartedDate;
                    return new Date(r.CourseStartedDate) < new Date(earliest) ? r.CourseStartedDate : earliest;

                }, null),
                HighestScore: validScores.length > 0 ? Math.max(...validScores) : null,
                TimeSpentInMinutes: records.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0)
            };

        });

        // Find earliest started date (could be CourseStartedDate or earliest LastAccess)
        const dates = allRows
            .map((row: any) => row.CourseStartedDate || row.CourseLastAccessDate)
            .filter((d: any) => d)
            .map((d: any) => new Date(d));
        const startedDate = dates.length > 0
            ? formatDate(new Date(Math.min(...dates.map((d: any) => d.getTime()))))
            : '---';

        // Calculate progress based on average score across resources
        const progress = allRows.length > 0
            ? Math.round(allRows.reduce((sum: number, row: any) => sum + normalizeScore(row.HighestScore), 0) / allRows.length)
            : 0;

        // Check if all items are completed
        const completedCount = allRows.filter((row: any) => row.CourseDateCompleted).length;
        const allCompleted = completedCount === allRows.length;
        const completedDate = allCompleted
            ? formatDate(allRows.reduce((latest: Date | null, row: any) => {

                if (!row.CourseDateCompleted) return latest;
                const date = new Date(row.CourseDateCompleted);
                return !latest || date > latest ? date : latest;

            }, null as Date | null))
            : null;

        // Calculate total time spent
        const totalMinutes = allRows.reduce((sum: number, row: any) => sum + (row.TimeSpentInMinutes || 0), 0
        );
        const timeSpent = calculateHourMinFormat(Math.round(totalMinutes));

        // Find most recent last access
        const lastAccessDates = allRows
            .map((row: any) => row.CourseLastAccessDate)
            .filter((d: any) => d)
            .map((d: any) => new Date(d));
        const lastAccess = lastAccessDates.length > 0
            ? formatDate(new Date(Math.max(...lastAccessDates.map((d: any) => d.getTime()))))
            : '---';

        return {
            completedDate,
            lastAccess,
            progress,
            startedDate,
            timeSpent
        };

    }, [reportSections]);

    return (
        <>
            <CourseSummaryCard
                completedDate={summaryData.completedDate}
                lastAccess={summaryData.lastAccess}
                progress={summaryData.progress}
                startedDate={summaryData.startedDate}
                timeSpent={summaryData.timeSpent}
            />

            <Card className="mt-4">
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <div className="d-flex">
                        <GMetrixDropdown
                            multiSelect
                            dataItemKey="value"
                            dataItems={resourceTypeLabels}
                            filterable={false}
                            label={formatMessage({ id: 'show_course_resources.a7ff7100', defaultMessage: 'Show Course Resources' })}
                            onChange={e => setState({ showResourcesSelected: e as any })}
                            style={{ maxWidth: '250px' }}
                            textField="name"
                            value={showResourcesSelected}
                        />
                    </div>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover className="kendo-table sticky-table-header mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'ResourceTypeID' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 150, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <span className="d-flex justify-content-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })}>
                                            <FormattedMessage defaultMessage="Resource" id="resource.6e70fb89" />
                                        </span>,
                                        'ResourceTypeID',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'CourseDateCompleted' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'current_completion_status_for_this_resou.bbfa3e3c', defaultMessage: 'Current completion status for this resource or section: Not Started, In Progress, or Completed.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Status" id="status.ad856ad7" />
                                            </span>
                                        </ReportToolTip>,
                                        'CourseDateCompleted',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'HighestScore' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'for_a_resource_row_the_highest_score_thi.204d070d', defaultMessage: "For a resource row: the highest score this student has earned on that resource. For a section row: the average of the student's highest scores across all scored resources in that section." })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Score" id="score.6ed6415a" />
                                            </span>
                                        </ReportToolTip>,
                                        'HighestScore',
                                        sort
                                    )}
                                </th>
                                {/* Dont delete this will be brought back later */}
                                {/* Average Time and Average Score columns */}
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'TimeSpentInMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'total_time_the_student_has_spent_on_cour.df26e8d8', defaultMessage: 'Total time the student has spent on course resources within the selected Date Window.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Time Spent" id="time_spent.da775c0c" />
                                            </span>
                                        </ReportToolTip>,
                                        'TimeSpentInMinutes',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'Attempts' }] })}
                                    style={{ cursor: 'pointer', minWidth: 120 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'for_a_resource_row_the_number_of_attempt.518fd677', defaultMessage: "For a resource row: the number of attempts this student has on this resource. For a section row: 'X of Y' shows how many visible resources in that section the student has attempted at least once." })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Attempts" id="attempts.ae7c8eee" />
                                            </span>
                                        </ReportToolTip>,
                                        'Attempts',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'CourseLastAccessDate' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'most_recent_student_interaction_for_this.df361920', defaultMessage: 'Most recent student interaction for this resource or section.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Last Access" id="last_access.6fd7ff0b" />
                                            </span>
                                        </ReportToolTip>,
                                        'CourseLastAccessDate',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((item: any, rowIndex: number) => (
                                <tr key={`${item.ResourceName}-${rowIndex}`}>
                                    <td className="text-center" style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                        <ReportToolTip copyText={item.ResourceName} id={`${item.ResourceName}-current-metric`} tooltip={item.ResourceName} trigger="hover">
                                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.ResourceName}
                                            </span>
                                        </ReportToolTip>
                                    </td>
                                    <td className="text-center">
                                        {renderStatus(getStatus(item))}
                                    </td>
                                    <td className="text-center">
                                        {(item.Attempts === 0 || !item.Attempts) ? <FormattedMessage defaultMessage="No Score" id="no_score.defd94f7" /> : (item.HighestScore ? renderScore(item.HighestScore) : '---')}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(item.TimeSpentInMinutes ?? 0)}
                                    </td>
                                    <td className="text-center">
                                        {item.Attempts ? item.Attempts + 'x' : '---'}
                                    </td>
                                    <td className="text-center">
                                        {formatDate(item.CourseLastAccessDate)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

            </Card>
        </>
    );

};